/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import useDebouncedFormValue from 'app/hooks/useDebouncedFormValue';
import { Button } from 'antd';
import { ChartStyleConfig } from 'app/types/ChartConfig';
import { FC, memo, useCallback, useEffect } from 'react';
import styled from 'styled-components/macro';
import { SPACE_SM, SPACE_XS } from 'styles/StyleConstants';
import { itemLayoutComparer } from '../../utils';
import { ItemLayoutProps } from '../../types';
import { LineTemplate } from './ChartTemplateConfig';
import { TemplateEditor } from './TemplateEditor';

const hasSavedTemplate = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const getInitialTemplate = (value: unknown) =>
  hasSavedTemplate(value) ? value : LineTemplate;

const ChartTemplatePanel: FC<ItemLayoutProps<ChartStyleConfig>> = memo(
  ({ ancestors, data, onChange, translate: t = title => title }) => {
    const { options } = data;
    const [template, debouncedUpdateValue] = useDebouncedFormValue<string>(
      getInitialTemplate(data?.value),
      {
        ancestors,
        needRefresh: options?.needRefresh,
        delay: 500,
      },
      onChange,
    ) as [string, (value: string) => void];

    useEffect(() => {
      if (!hasSavedTemplate(data?.value)) {
        onChange?.(ancestors, LineTemplate, options?.needRefresh);
      }
    }, [ancestors, data?.value, onChange, options?.needRefresh]);

    const handleTemplateChange = useCallback(
      (value: string | undefined) => {
        if (value !== undefined) {
          debouncedUpdateValue(value);
        }
      },
      [debouncedUpdateValue],
    );

    const handleReset = useCallback(() => {
      debouncedUpdateValue(LineTemplate);
      onChange?.(ancestors, LineTemplate);
    }, [ancestors, debouncedUpdateValue, onChange]);

    return (
      <StyledChartTemplatePanel>
        <Button size="small" onClick={handleReset}>
          {t('global.button.restore', true)}
        </Button>
        <TemplateEditor value={template} onChange={handleTemplateChange} />
      </StyledChartTemplatePanel>
    );
  },
  itemLayoutComparer,
);

const StyledChartTemplatePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_XS};
  width: 100%;
  margin-top: ${SPACE_SM};
`;

export default ChartTemplatePanel;
